import { getRawToken } from '@kinde-oss/kinde-auth-react/utils'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { API_BASE_URL } from '@/common/config/api'

/**
 * Adds the Kinde access token to a request's headers. Shared by the RTK Query base query and by
 * requests that must go through axios, such as attachment uploads that report progress.
 *
 * @param {Headers} headers - The request headers
 * @returns {Promise<Headers>} The same headers, with auth added when a token is available
 */
export const prepareAuthHeaders = async (headers) => {
    try {
        // Attempt to get auth token - may fail if Kinde SDK not yet initialized
        // This is expected on initial page load, especially for public endpoints
        // like subdomain lookup that don't require authentication
        const token = await getRawToken('accessToken')

        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
            headers.set('Authorization-Scheme', 'KindeAuth')
        }
    } catch {
        // Continue without auth header - some endpoints (like subdomain lookup) don't require it
        // Authenticated endpoints will return 401 which is handled by the error boundaries
    }
    return headers
}

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: prepareAuthHeaders,
})

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
    /**
     * `reducerPath` is optional and will not be required by most users.
     * This is useful if you have multiple API definitions,
     * e.g. where each has a different domain, with no interaction between endpoints.
     * Otherwise, a single API definition should be used in order to support tag invalidation,
     * among other features
     */
    reducerPath: 'api',
    /**
     * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
     */
    baseQuery,
    /**
     * Tag types must be defined in the original API definition
     * for any tags that would be provided by injected endpoints
     */
    tagTypes: [],
    /**
     * This api has endpoints injected in adjacent files,
     * which is why no endpoints are shown below.
     * If you want all endpoints defined in the same file, they could be included here instead
     */

    endpoints: () => ({}),
})
